import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { productImageUrl } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useCurrency } from "../context/CurrencyContext";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";

export default function CartPage() {
  const { items, updateQty, removeItem, totals } = useCart();
  const { t } = useI18n();
  const { format: formatGdes } = useCurrency();
  const nav = useNavigate();
  return (
    <div className="je-container py-10" data-testid="cart-page">
      <h1 className="text-3xl md:text-4xl font-bold mb-8" style={{fontFamily:"Outfit"}}>{t("myCart")}</h1>
      {items.length === 0 ? (
        <div className="text-center py-20 bg-gray-50 rounded-xl">
          <ShoppingBag className="w-12 h-12 text-gray-400 mx-auto" />
          <p className="text-gray-500 mt-3">{t("emptyCart")}</p>
          <Link to="/products" className="je-btn-blue inline-block mt-5">{t("startShopping")}</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          <div className="space-y-3">
            {items.map((it) => {
              const idf = it.line_id || it.product_id;
              return (
              <div key={idf} className="flex items-center gap-4 border rounded-xl p-3 md:p-4 bg-white" data-testid={`cart-item-${idf}`}>
                <Link to={`/products/${it.product_id}`} className="w-20 h-20 md:w-24 md:h-24 bg-[#F3F4F6] rounded-lg flex items-center justify-center overflow-hidden">
                  <img src={productImageUrl(it.image)} alt={it.name} className="max-h-full max-w-full object-contain p-2" />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/products/${it.product_id}`} className="font-semibold hover:text-[#0052CC] line-clamp-2">{it.name}</Link>
                  <p className="text-sm text-gray-500 mt-0.5">{formatGdes(it.price)}{it.color_breakdown ? " / pied" : it.unit_of_sale && it.unit_of_sale !== "unit" ? ` / ${it.unit_of_sale}` : ""}</p>
                  {it.color_breakdown && (
                    <ul className="mt-1 text-xs text-gray-600" data-testid={`cart-color-breakdown-${idf}`}>
                      {Object.entries(it.color_breakdown).map(([name, q]) => (
                        <li key={name}>• {name} : <b>{q}</b> pied(s)</li>
                      ))}
                    </ul>
                  )}
                  {it.selected_options && Object.keys(it.selected_options).length > 0 && (
                    <ul className="mt-1 text-xs text-purple-700" data-testid={`cart-options-${idf}`}>
                      {Object.entries(it.selected_options).map(([k, v]) => (
                        <li key={k}>• {k} : <b>{v}</b></li>
                      ))}
                    </ul>
                  )}
                  {Number(it.technician_qty) > 0 && (
                    <p className="mt-1 text-xs text-amber-700 font-semibold" data-testid={`cart-technician-${idf}`}>
                      + {it.technician_qty} technicien(s) : {formatGdes(it.technician_qty * it.technician_price)}
                    </p>
                  )}
                  {!it.color_breakdown && (
                    <div className="inline-flex border rounded-md mt-2">
                      <button onClick={() => updateQty(idf, it.quantity - 1)} className="px-2 py-1"><Minus className="w-3.5 h-3.5" /></button>
                      <span className="px-3 py-1 text-sm font-semibold">{it.quantity}</span>
                      <button onClick={() => updateQty(idf, it.quantity + 1)} className="px-2 py-1"><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-bold" style={{fontFamily:"Outfit"}}>{formatGdes(it.price * it.quantity + (Number(it.technician_qty) || 0) * (Number(it.technician_price) || 0))}</p>
                  <button onClick={() => removeItem(idf)} className="text-red-600 text-sm flex items-center gap-1 mt-2 hover:underline" data-testid={`remove-${idf}`}>
                    <Trash2 className="w-4 h-4" /> {t("remove")}
                  </button>
                </div>
              </div>
            );})}
          </div>
          <aside className="border rounded-xl p-5 h-fit bg-white sticky top-44">
            <h3 className="font-bold mb-4" style={{fontFamily:"Outfit"}}>{t("cartSummary")}</h3>
            <div className="flex justify-between text-sm mb-2"><span>{t("subtotal")}</span><span className="font-semibold">{formatGdes(totals.subtotal)}</span></div>
            <div className="flex justify-between text-sm mb-2 text-gray-500"><span>{t("deliveryFee")}</span><span>—</span></div>
            <button onClick={() => nav("/checkout")} className="je-btn-primary w-full mt-5" data-testid="cart-checkout-btn">
              {t("goToCheckout")}
            </button>
          </aside>
        </div>
      )}
    </div>
  );
}
